import type { FindingView } from "../../api/types.js";

export interface StandardReferenceView {
  label: string;
  system: string;
  url: string;
  description: string;
}

const OWASP_TOP_10_URLS: Readonly<Record<string, string>> = {
  A01: "https://owasp.org/Top10/A01_2021-Broken_Access_Control/",
  A02: "https://owasp.org/Top10/A02_2021-Cryptographic_Failures/",
  A03: "https://owasp.org/Top10/A03_2021-Injection/",
  A04: "https://owasp.org/Top10/A04_2021-Insecure_Design/",
  A05: "https://owasp.org/Top10/A05_2021-Security_Misconfiguration/",
  A06: "https://owasp.org/Top10/A06_2021-Vulnerable_and_Outdated_Components/",
  A07: "https://owasp.org/Top10/A07_2021-Identification_and_Authentication_Failures/",
  A08: "https://owasp.org/Top10/A08_2021-Software_and_Data_Integrity_Failures/",
  A09: "https://owasp.org/Top10/A09_2021-Security_Logging_and_Monitoring_Failures/",
  A10: "https://owasp.org/Top10/A10_2021-Server-Side_Request_Forgery_%28SSRF%29/",
};

function externalReferenceUrl(system: string, id: string, url: string | null): string | null {
  if (url?.startsWith("https://")) return url;
  const normalized = system.toLocaleLowerCase();
  if (normalized === "cve" && /^CVE-\d{4}-\d{4,}$/u.test(id)) {
    return `https://www.cve.org/CVERecord?id=${encodeURIComponent(id)}`;
  }
  if (
    normalized === "ghsa" &&
    /^GHSA-[23456789cfghjmpqrvwx]{4}(?:-[23456789cfghjmpqrvwx]{4}){2}$/iu.test(id)
  ) {
    return `https://github.com/advisories/${encodeURIComponent(id)}`;
  }
  if (normalized === "osv" && id.length <= 200) {
    return `https://osv.dev/vulnerability/${encodeURIComponent(id)}`;
  }
  return null;
}

export function findingStandards(finding: FindingView): StandardReferenceView[] {
  const references: StandardReferenceView[] = [];
  const cweMatch = /^CWE-(\d+)$/u.exec(finding.cwe);
  if (cweMatch?.[1]) {
    references.push({
      label: finding.cwe,
      system: "CWE",
      url: `https://cwe.mitre.org/data/definitions/${cweMatch[1]}.html`,
      description:
        "Weakness class for this code or design issue; it is not a product-specific CVE.",
    });
  }

  const owaspCode = /^(A\d{2}):2021/u.exec(finding.owasp)?.[1];
  const owaspUrl = owaspCode ? OWASP_TOP_10_URLS[owaspCode] : undefined;
  if (owaspUrl) {
    references.push({
      label: finding.owasp,
      system: "OWASP Top 10",
      url: owaspUrl,
      description:
        "Application-risk category correlated from the detector's governed rule metadata.",
    });
  }

  for (const reference of finding.externalReferences ?? []) {
    const url = externalReferenceUrl(reference.system, reference.id, reference.url);
    if (!url) continue;
    references.push({
      label: reference.id,
      system: reference.system.toLocaleUpperCase(),
      url,
      description:
        reference.system.toLocaleLowerCase() === "cve"
          ? "Published vulnerability identifier for the resolved component and affected version."
          : "Published advisory identifier supplied by the signed dependency or container feed.",
    });
  }
  return references;
}
